/**
 * LibraryIndicatorCalculator.js
 * 
 * NEW APPROACH: Uses battle-tested `technicalindicators` library
 * with AGGRESSIVE warm-up (500-1000 bars) for accurate EMA convergence.
 * 
 * This is kept separate from IndicatorCalculator.js so you can revert if needed.
 */

import { 
  SMA, 
  EMA, 
  WMA,
  RSI, 
  MACD, 
  BollingerBands,
  Stochastic,
  CCI,
  ADX,
  PSAR,
  VWAP as VWAPLib
} from 'technicalindicators';

/**
 * Minimum bars needed for accurate indicator calculation
 * These are AGGRESSIVE warm-up periods to match AlphaVantage/TradeStation accuracy
 * 
 * EMA Convergence Research:
 * - 86% accuracy: 3x period
 * - 95% accuracy: 10x period  
 * - 99% accuracy: 20x period
 * - 99.9% accuracy: 50x period
 * 
 * We use high multipliers to ensure values match what traders see on other platforms
 */
const WARM_UP_BARS = {
  SMA: (period) => Math.max(period, 50),        // SMA is instant, but fetch extra for context
  EMA: (period) => Math.max(period * 20, 500),  // 20x for 99%+ convergence
  WMA: (period) => Math.max(period, 50),
  DEMA: (period) => Math.max(period * 20, 500), // DEMA uses EMA internally
  TEMA: (period) => Math.max(period * 20, 500), // TEMA uses EMA internally  
  RSI: (period) => Math.max((period + 1) * 25, 500), // RSI uses Wilder's smoothing (EMA-like)
  MACD: (params) => Math.max(params.slowPeriod * 20, 600), // Use slow EMA's warm-up
  BBANDS: (period) => Math.max(period, 100),    // Uses SMA, but fetch more for stability
  STOCH: (params) => Math.max((params.fastKPeriod || 14) * 5, 150),
  CCI: (period) => Math.max(period * 3, 150),
  ADX: (period) => Math.max(period * 6, 200),   // ADX is very smoothed
  SAR: () => 150,
  VWAP: () => 100
};

/**
 * Get recommended number of bars to fetch for accurate calculation
 * Will return between minBars and 1000, or however many bars exist
 */
export function getRecommendedBarCount(indicatorType, params = {}) {
  // Default to 300 bars if no indicator type
  if (!indicatorType) {
    console.warn('[LibraryIndicatorCalculator] No indicator type provided, using default 300 bars');
    return 300;
  }
  
  const calculator = WARM_UP_BARS[indicatorType];
  
  if (!calculator) {
    console.warn(`[LibraryIndicatorCalculator] Unknown indicator: ${indicatorType}, using default 300 bars`);
    return 300;
  }
  
  // Extract period from various parameter formats
  const period = params?.period || params?.time_period || params?.slowPeriod || 14;
  
  // Calculate recommended bars
  let recommendedBars;
  try {
    // Pass the right argument based on what the calculator expects
    if (indicatorType === 'MACD' || indicatorType === 'STOCH') {
      recommendedBars = calculator(params);
    } else if (typeof calculator === 'function') {
      recommendedBars = calculator(period);
    } else {
      recommendedBars = 300;
    }
  } catch (error) {
    console.warn(`[LibraryIndicatorCalculator] Error calculating bars for ${indicatorType}:`, error);
    return 300;
  }
  
  // Validate result
  if (!recommendedBars || isNaN(recommendedBars) || recommendedBars <= 0) {
    return 300;
  }
  
  // Cap at 1000 bars (reasonable limit)
  return Math.min(Math.round(recommendedBars), 1000);
}

/**
 * Normalize indicator parameters to ensure proper types
 * Safety net for old saved indicators that might have string values
 */
function normalizeParams(params) {
  if (!params || typeof params !== 'object') {
    return {};
  }
  
  const normalized = {};
  
  for (const [key, value] of Object.entries(params)) {
    // Skip null/undefined
    if (value == null) {
      continue;
    }
    
    // Convert string numbers to actual numbers (for backwards compatibility)
    if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
      normalized[key] = parseFloat(value);
    } else {
      normalized[key] = value;
    }
  }
  
  return normalized;
}

/**
 * Get the correct price series based on series_type parameter
 * Respects user's choice of open/high/low/close
 * 
 * Used by: SMA, EMA, WMA, DEMA, TEMA, RSI, MACD, BBANDS
 * Not used by: STOCH, CCI, ADX, SAR, VWAP (require specific OHLC combinations)
 */
function getPriceSeries(bars, seriesType = 'close') {
  const type = String(seriesType).toLowerCase();
  
  switch (type) {
    case 'open':
      return bars.map(b => b.open);
    case 'high':
      return bars.map(b => b.high);
    case 'low':
      return bars.map(b => b.low);
    case 'close':
    default:
      return bars.map(b => b.close);
  }
}

/**
 * Calculate indicator values using the library
 * Returns array of { time, value } or { time, ...values } for multi-value indicators
 */
export function calculateIndicator(indicatorType, params, bars) {
  // Normalize params as safety net (should already be correct numbers now)
  const p = normalizeParams(params);
  
  if (!bars || bars.length === 0) {
    return [];
  }
  
  // Prepare input arrays
  const opens = bars.map(b => b.open);
  const highs = bars.map(b => b.high);
  const lows = bars.map(b => b.low);
  const closes = bars.map(b => b.close);
  const volumes = bars.map(b => b.volume || 0);
  
  // Get the correct price series based on user's series_type preference
  const values = getPriceSeries(bars, p.series_type);
  
  let results = [];
  
  try {
    switch (indicatorType) {
      case 'SMA':
        results = SMA.calculate({
          period: p.time_period || p.period || 20,
          values
        });
        break;
        
      case 'EMA':
        results = EMA.calculate({
          period: p.time_period || p.period || 14,
          values
        });
        break;
        
      case 'WMA':
        results = WMA.calculate({
          period: p.time_period || p.period || 20,
          values
        });
        break;
        
      case 'DEMA':
        // DEMA = 2*EMA(n) - EMA(EMA(n))
        const demaPeriod = p.time_period || p.period || 14;
        const ema1 = EMA.calculate({ period: demaPeriod, values });
        const ema2 = EMA.calculate({ period: demaPeriod, values: ema1 });
        
        // Calculate DEMA for the overlapping portion
        const demaStartIdx = ema1.length - ema2.length;
        results = ema2.map((emaOfEma, idx) => {
          const emaValue = ema1[demaStartIdx + idx];
          return 2 * emaValue - emaOfEma;
        });
        break;
        
      case 'TEMA':
        // TEMA = 3*EMA(n) - 3*EMA(EMA(n)) + EMA(EMA(EMA(n)))
        const temaPeriod = p.time_period || p.period || 14;
        const tema_ema1 = EMA.calculate({ period: temaPeriod, values });
        const tema_ema2 = EMA.calculate({ period: temaPeriod, values: tema_ema1 });
        const tema_ema3 = EMA.calculate({ period: temaPeriod, values: tema_ema2 });
        
        // Calculate TEMA for the overlapping portion
        const tema2StartIdx = tema_ema1.length - tema_ema2.length;
        const tema3StartIdx = tema_ema2.length - tema_ema3.length;
        
        results = tema_ema3.map((ema3, idx) => {
          const ema1Value = tema_ema1[tema2StartIdx + tema3StartIdx + idx];
          const ema2Value = tema_ema2[tema3StartIdx + idx];
          return 3 * ema1Value - 3 * ema2Value + ema3;
        });
        break;
        
      case 'RSI':
        results = RSI.calculate({
          period: p.time_period || p.period || 14,
          values
        });
        break;
        
      case 'MACD':
        const macdResults = MACD.calculate({
          values,
          fastPeriod: p.fastperiod || 12,
          slowPeriod: p.slowperiod || 26,
          signalPeriod: p.signalperiod || 9,
          SimpleMAOscillator: false,
          SimpleMASignal: false
        });
        // Convert to time-series format with correct field names for parser
        const macdStartIdx = bars.length - macdResults.length;
        return macdResults.map((result, idx) => ({
          time: bars[macdStartIdx + idx].time,
          MACD: result.MACD,
          MACD_Signal: result.signal,  // ✅ Parser expects MACD_Signal
          MACD_Hist: result.histogram   // ✅ Parser expects MACD_Hist
        }));
        
      case 'BBANDS':
        const bbandsResults = BollingerBands.calculate({
          period: p.time_period || p.period || 20,
          stdDev: p.nbdevup || 2,
          values
        });
        const bbandsStartIdx = bars.length - bbandsResults.length;
        return bbandsResults.map((result, idx) => ({
          time: bars[bbandsStartIdx + idx].time,
          upper: result.upper,
          middle: result.middle,
          lower: result.lower
        }));
        
      case 'STOCH':
        // Stochastic uses: period (for %K lookback), signalPeriod (for %D smoothing)
        const stochResults = Stochastic.calculate({
          high: highs,
          low: lows,
          close: closes,
          period: p.fastkperiod || 14,     // %K period (lookback)
          signalPeriod: p.slowdperiod || 3  // %D period (smoothing of %K)
        });
        const stochStartIdx = bars.length - stochResults.length;
        return stochResults.map((result, idx) => ({
          time: bars[stochStartIdx + idx].time,
          slowK: result.k,  // ✅ Parser expects slowK
          slowD: result.d   // ✅ Parser expects slowD
        }));
        
      case 'CCI':
        results = CCI.calculate({
          open: opens,
          high: highs,
          low: lows,
          close: closes,
          period: p.time_period || p.period || 20
        });
        break;
        
      case 'ADX':
        const adxResults = ADX.calculate({
          high: highs,
          low: lows,
          close: closes,
          period: p.time_period || p.period || 14
        });
        const adxStartIdx = bars.length - adxResults.length;
        return adxResults.map((result, idx) => ({
          time: bars[adxStartIdx + idx].time,
          value: result.adx  // ✅ Parser expects 'value' field
        }));
        
      case 'SAR':
        const sarResults = PSAR.calculate({
          high: highs,
          low: lows,
          step: p.acceleration || 0.02,
          max: p.maximum || 0.2
        });
        const sarStartIdx = bars.length - sarResults.length;
        return sarResults.map((result, idx) => ({
          time: bars[sarStartIdx + idx].time,
          value: result
        }));
        
      case 'VWAP':
        // VWAP should reset at the start of each trading session
        // Calculate: cumulative(typical_price * volume) / cumulative(volume)
        // where typical_price = (high + low + close) / 3
        
        results = [];
        let cumulativeTPV = 0; // cumulative (typical price * volume)
        let cumulativeVolume = 0;
        let lastDate = null;
        
        bars.forEach((bar, idx) => {
          // Get the date (reset at midnight)
          const barDate = new Date(bar.time * 1000);
          const dateStr = barDate.toISOString().split('T')[0]; // YYYY-MM-DD
          
          // Reset cumulative values at start of new trading day
          if (dateStr !== lastDate) {
            cumulativeTPV = 0;
            cumulativeVolume = 0;
            lastDate = dateStr;
          }
          
          // Calculate typical price
          const typicalPrice = (highs[idx] + lows[idx] + closes[idx]) / 3;
          
          // Accumulate
          cumulativeTPV += typicalPrice * volumes[idx];
          cumulativeVolume += volumes[idx];
          
          // Calculate VWAP
          const vwap = cumulativeVolume > 0 ? cumulativeTPV / cumulativeVolume : typicalPrice;
          results.push(vwap);
        });
        break;
        
      default:
        console.warn(`[LibraryIndicatorCalculator] Unsupported indicator: ${indicatorType}`);
        return [];
    }
    
    // For single-value indicators, map to time-series format
    if (Array.isArray(results) && typeof results[0] === 'number') {
      const startIdx = bars.length - results.length;
      return results.map((value, idx) => ({
        time: bars[startIdx + idx].time,
        value
      }));
    }
    
    return results;
    
  } catch (error) {
    console.error(`[LibraryIndicatorCalculator] Error calculating ${indicatorType}:`, error);
    return [];
  }
}

/**
 * Check if we have enough bars for accurate calculation
 */
export function hasEnoughBars(indicatorType, params, barCount) {
  const recommended = getRecommendedBarCount(indicatorType, params);
  return barCount >= Math.min(recommended, 200); // At least 200 bars or recommended, whichever is less
}

/**
 * Get a display-friendly message about bar requirements
 */
export function getBarRequirementMessage(indicatorType, params, currentBars) {
  const recommended = getRecommendedBarCount(indicatorType, params);
  const minimum = Math.min(200, recommended);
  
  if (currentBars >= recommended) {
    return `Excellent: ${currentBars}/${recommended} bars (fully warmed up)`;
  } else if (currentBars >= minimum) {
    return `Good: ${currentBars}/${recommended} bars (acceptable accuracy)`;
  } else {
    return `Limited: ${currentBars}/${minimum} bars needed for accurate calculation`;
  }
}

